//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { ReactComponent as Exclamation } from 'assets/exclamation-fill.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import { TriggerAttributionMessage } from 'components/TriggerAttributionMessage';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useModal } from 'contexts/ModalProvider';
import { useToast } from 'contexts/ToastProvider';
import { ControllerFwUpgradeProgress } from 'models/controller-fw-upgrade-progress';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 將十進制數字轉換為四位十六進制表示
 * @param {number} decimal - 要轉換的十進制數字
 * @returns {string} - 四位十六進制表示
 */
export const decimalToHex = (decimal: number) => {
  if (decimal >= 0) {
    // 正數直接轉換並補零到4位
    return decimal.toString(16).toUpperCase().padStart(4, '0');
  } else {
    // 負數特殊處理：使用標準的 16 位元二補數表示法，轉十六進制，並保證總共4位
    return (0x10000 + decimal).toString(16).toUpperCase().padStart(4, '0');
  }
};

/* 重開機期間裝置不再回報進度,改由前端自行推進到 99% 並停住,等 API-ready 檢查完成 */
const REBOOT_PROGRESS_TARGET = 99;
const REBOOT_PROGRESS_DURATION = 3 * 60 * 1000; // 對齊 useSseEventTrigger 的 restartTime

/*
 * HTTP 400 時 AuthProvider 會直接拋出後端回應的 JSON body(內含 ErrorDescription),
 * 但網路錯誤或程式自身的例外並沒有這個欄位,存取前必須逐層確認。
 */
const getErrorDescription = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { ErrorDescription } = error as { ErrorDescription?: unknown };
  return typeof ErrorDescription === 'string' ? ErrorDescription : undefined;
};

const useControllerFwUpgradeProgress = () => {
  const TIMEOUT_DURATION = 15 * 60 * 1000; // 15 分鐘
  const CHECK_INTERVAL = 5 * 1000; // 5 秒
  const { t } = useTranslation();
  const { rootControllerSerialNumber } = useConfig();
  const { getCbtControllerFwUpgradeProgress } = useAuth();
  const { addConfirmModal, addCustomModal } = useModal();
  const { addToast } = useToast();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef(false);
  const isPausedRef = useRef(false);
  const triggerUserRef = useRef<string | undefined>();
  const triggerClientIdRef = useRef<string | undefined>();
  const rebootProgressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const rebootProgressStartedRef = useRef(false);

  // 存儲回調函數的引用
  const successCallbackRef = useRef<(() => void) | null>(null);
  const errorCallbackRef = useRef<(() => void) | null>(null);

  const progressRef = useRef<ControllerFwUpgradeProgress>({
    progressValue: 0,
    progressType: 0,
    timestamp: 0,
  });

  // 設置成功回調的函數
  const onUpgradeSuccess = (callback: () => void) => {
    successCallbackRef.current = callback;
  };

  // 設置錯誤回調的函數
  const onUpgradeError = (callback: () => void) => {
    errorCallbackRef.current = callback;
  };

  const handleCustomModal = ({
    isOpen = false,
    name = 'endControllerCustomModal',
    content = <div></div>,
  }: {
    isOpen?: boolean;
    name?: string;
    content?: React.ReactNode;
  }) => {
    addCustomModal({
      open: isOpen,
      name,
      content: content,
      width: 700,
    });
  };

  const endRadialProgressModal = () => handleCustomModal({});

  const updateAndDisplayRadialProgressModal = (progressValue: number, triggerUser?: string, triggerClientId?: string) => {
    const style = {
      '--value': progressValue,
      '--size': '6rem',
      '--thickness': '6px',
    } as React.CSSProperties;
    const displayTriggerUser = triggerUser ?? triggerUserRef.current;
    const displayTriggerClientId = triggerClientId ?? triggerClientIdRef.current;
    const content = (
      <div className="flex flex-col items-center space-y-1">
        <h3 className="font-bold text-lg">{t('modal.firmwareUpgrade')}</h3>
        <TriggerAttributionMessage action="upgrade" triggerUser={displayTriggerUser} triggerClientId={displayTriggerClientId} />
        <p className="pb-4">{t('modal.firmwareUpgradeMessage')}</p>
        <div className="radial-progress" style={style}>
          <div className="flex justify-center items-center w-[92px] h-[92px] rounded-full border-gray-300 border-2">{progressValue}%</div>
        </div>
        <div className="flex items-center">
          <Exclamation className="fill-yellow-500 mr-2 w-8" />
          <p className="text-gray-500">{t('modal.firmwareUpgradeTip')}</p>
        </div>
      </div>
    );
    handleCustomModal({
      isOpen: true,
      name: 'controllerUpgradeRadialProgressModal',
      content,
    });
  };

  const stopRebootProgress = () => {
    if (rebootProgressTimerRef.current) {
      clearInterval(rebootProgressTimerRef.current);
      rebootProgressTimerRef.current = null;
    }
  };

  /* 進入重開機階段:自行把進度推進到 99% 後停住，等 API-ready 檢查完成才收尾 */
  const startRebootProgress = (fromValue: number, triggerUser?: string, triggerClientId?: string) => {
    if (rebootProgressStartedRef.current) return; // 只啟動一次
    rebootProgressStartedRef.current = true;

    let current = Math.min(Math.max(fromValue, 0), REBOOT_PROGRESS_TARGET);
    updateAndDisplayRadialProgressModal(current, triggerUser, triggerClientId);

    const steps = REBOOT_PROGRESS_TARGET - current;
    if (steps <= 0) return; // 已達 99%,直接停住

    rebootProgressTimerRef.current = setInterval(() => {
      current += 1;
      if (current >= REBOOT_PROGRESS_TARGET) {
        current = REBOOT_PROGRESS_TARGET;
        stopRebootProgress();
      }
      updateAndDisplayRadialProgressModal(current, triggerUser, triggerClientId);
    }, REBOOT_PROGRESS_DURATION / steps);
  };

  const displayErrorModal = (status: string, { progressType, progressValue }: ControllerFwUpgradeProgress) => {
    addConfirmModal({
      open: true,
      id: 'controllerUpgradeFailModal',
      name: 'controllerUpgradeFail',
      btnOk: t('modal.btnOk'),
      type: 'error',
      title: t('toast.upgradeFailed'),
      message: (
        <span>
          {t('modal.controller.failMessage', {
            status: status,
            errorCode: `0x${decimalToHex(progressType)}${decimalToHex(progressValue)}`,
          })}
        </span>
      ),
      onClickOk: () => {
        // 執行錯誤回調
        if (errorCallbackRef.current) {
          console.log('excute onUpgradeError');
          errorCallbackRef.current();
        }
      },
    });
  };

  // 停止所有計時器
  const stopAllTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // NOTE: 直接操作 ref 而非呼叫 stopRebootProgress(),讓 stopAllTimers 維持「只依賴 ref」,
    // 否則 unmount 的 useEffect 會被 react-hooks/exhaustive-deps 判定缺少依賴。
    if (rebootProgressTimerRef.current) {
      clearInterval(rebootProgressTimerRef.current);
      rebootProgressTimerRef.current = null;
    }
    rebootProgressStartedRef.current = false;
    isRunningRef.current = false;
  };

  // 重置計時器
  const resetTimeoutTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      console.log('Disable timer when data remains unchanged');
      stopAllTimers();
      endRadialProgressModal();

      displayErrorModal(t('modal.controller.statusTimeout'), progressRef.current);
    }, TIMEOUT_DURATION);
  };

  const handleUpgradeSuccess = () => {
    endRadialProgressModal();
    if (successCallbackRef.current) {
      console.log('execute onUpgradeSuccess');
      successCallbackRef.current();
    }
    stopAllTimers();
  };

  const handleProgressUpdate = (newProgress: ControllerFwUpgradeProgress, triggerUser?: string, triggerClientId?: string) => {
    const currentProgress = progressRef.current;

    // progressType = -1 為無須更新，顯示成功並登出
    if (newProgress.progressType === -1) {
      handleUpgradeSuccess();
      return;
    }
    // 檢查 progressType 是否為負值 (錯誤情況)
    if (newProgress.progressType < 0) {
      console.log('Stop all timers when detecting error type (negative progressType)');
      // 立即更新進度狀態
      progressRef.current = newProgress;
      stopAllTimers();
      endRadialProgressModal();
      displayErrorModal(t('modal.controller.statusFailed'), newProgress);
      return;
    }

    // 檢查進度是否有變化
    console.log('newProgress', JSON.stringify(newProgress, null, 2));
    console.log('currentProgress', JSON.stringify(currentProgress, null, 2));
    const hasUpdated =
      newProgress.timestamp > currentProgress.timestamp ||
      newProgress.progressType !== currentProgress.progressType ||
      newProgress.progressValue !== currentProgress.progressValue;
    console.log('progress updated?', hasUpdated);

    if (hasUpdated) {
      // progressValue 到 100，或 progressType 從 3 => 0，表示進度完成
      if (newProgress.progressValue >= 100 || (newProgress.progressType === 0 && progressRef.current.progressType === 3)) {
        handleUpgradeSuccess();
        return;
      }

      // 更新進度Modal(重開機動畫進行中不覆寫,避免把 99% 打回裝置最後回報值)
      if (!isPausedRef.current && !rebootProgressStartedRef.current) {
        updateAndDisplayRadialProgressModal(newProgress.progressValue, triggerUser, triggerClientId);
      }
      progressRef.current = newProgress;

      console.log('Reset timer when progress changes');
      resetTimeoutTimer();
    }
  };

  // 檢查進度
  const checkProgress = async () => {
    try {
      if (isPausedRef.current) {
        console.log('⏸️ Progress check paused, skipping modal update');
        return;
      }

      const newProgress = await getCbtControllerFwUpgradeProgress(rootControllerSerialNumber);
      handleProgressUpdate(newProgress);
    } catch (error: unknown) {
      // 裝置在重開機時，API return TypeError/SyntaxError: Failed to fetch，可忽略
      if (error instanceof TypeError || error instanceof SyntaxError) return;

      const errorDescription = getErrorDescription(error);
      // 裝置升級後，OWGW 未支援 getCbtControllerFwUpgradeProgress() API，可忽略，視為更新成功
      if (errorDescription?.includes('Unsupported HTTP Method')) {
        handleUpgradeSuccess();
        return;
      }

      console.error('Error occurred while checking progress:', error);
      addToast({
        title: t('toast.upgradeFailed'),
        message: errorDescription ?? '',
        color: 'bg-toast-error-50',
        icon: ErrorCircle,
        text: 'text-toast-error-800',
        svg: 'toastIcon-error',
      });
    }
  };

  const pauseProgressCheck = () => {
    console.log('⏸️ Pausing progress modal updates');
    isPausedRef.current = true;
  };

  const resumeProgressCheck = () => {
    console.log('▶️ Resuming progress modal updates');
    isPausedRef.current = false;
    // 恢復時立即檢查一次進度
    checkProgress();
  };

  const startTimer = (triggerUser?: string, options: { polling?: boolean; triggerClientId?: string } = {}) => {
    const { polling = true, triggerClientId } = options;
    if (isRunningRef.current) return; // 如果已經運行中，則不重複啟動

    isRunningRef.current = true; // 設置為運行中
    isPausedRef.current = false;
    triggerUserRef.current = triggerUser;
    triggerClientIdRef.current = triggerClientId;
    stopRebootProgress();
    rebootProgressStartedRef.current = false;

    // 重置重置計時器 ref 狀態
    progressRef.current = {
      progressValue: 0,
      progressType: 0,
      timestamp: 0,
    };

    updateAndDisplayRadialProgressModal(0, triggerUser, triggerClientId);
    resetTimeoutTimer();
    checkProgress();

    // 設置每分鐘檢查的間隔計時器(主動輪詢)
    if (polling) {
      intervalRef.current = setInterval(checkProgress, CHECK_INTERVAL);
    }
  };

  // 手動停止計時器 - 提供給外部按鈕使用
  const stopTimer = () => {
    stopAllTimers();
  };

  const updateProgressFromEvent = (progress: ControllerFwUpgradeProgress, triggerUser?: string, triggerClientId?: string) => {
    if (triggerUser) triggerUserRef.current = triggerUser;
    if (triggerClientId) triggerClientIdRef.current = triggerClientId;
    handleProgressUpdate(progress, triggerUser, triggerClientId);
  };

  // 停止getTopology的計時器
  const stopGetTopologyTimer = (topologyTimeRef: React.MutableRefObject<NodeJS.Timeout | undefined>) => {
    if (topologyTimeRef.current) {
      console.log('Stop getTopology timer');
      clearTimeout(topologyTimeRef.current);
      topologyTimeRef.current = undefined; // 清除引用
    }
  };

  // 組件卸載時清理計時器
  useEffect(() => {
    return () => {
      stopAllTimers();
    };
  }, []);

  return {
    progressRef,
    startTimer,
    stopTimer,
    pauseProgressCheck,
    resumeProgressCheck,
    stopGetTopologyTimer,
    onUpgradeSuccess,
    onUpgradeError,
    endRadialProgressModal,
    updateAndDisplayRadialProgressModal,
    updateProgressFromEvent,
    startRebootProgress,
  };
};

export default useControllerFwUpgradeProgress;
